import { useState } from 'react'
import { useReadWorkflow } from '@opallabs/api'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { ThemeProvider } from '@opallabs/hooks'
import { DotLoader, Icon, WorkflowIcon } from '@opallabs/lapidary'
import { getApprovalContent, getDeliveryContent } from '@opallabs/plasma'
import { Content } from '@opallabs/wormhole/types'
import * as styles from './styles'

type WorkflowStatusSummaryProps = {
  content: Content
  finalApprovalReached?: boolean
  onStatusClick: () => void
}

const WorkflowStatusSummaryComponent = ({
  content,
  onStatusClick,
}: WorkflowStatusSummaryProps) => {
  const { data, isLoading } = useReadWorkflow(
    content.id,
    content.account ? ((content.account as any).id as number) : undefined
  )

  const workflowStarted = data && data.status ? data.status !== 'new' : false

  const approvalPhases = data
    ? data.phases.filter(
        (phase) =>
          phase && phase.phase_type && phase.phase_type.match(/approval/)
      )
    : []

  const finalApprovalReached =
    !!approvalPhases.length &&
    approvalPhases[approvalPhases.length - 1].attributes.is_complete

  const { status: approvalStatus, description: approvalDescription } =
    getApprovalContent({
      content,
      phases: data ? data.phases : [],
      workflowStarted,
      finalApprovalReached,
    })

  const { status: deliveryStatus, description: deliveryDescription } =
    getDeliveryContent(content, data ? data.users : [])

  const [approvalHovered, setApprovalHovered] = useState(false)
  const [deliveryHovered, setDeliveryHovered] = useState(false)

  return isLoading ? (
    <div css={styles.summaryContainer}>
      <DotLoader />
    </div>
  ) : (
    <div css={styles.summaryContainer}>
      <button
        css={styles.summaryItem}
        onFocus={() => setApprovalHovered(true)}
        onBlur={() => setApprovalHovered(false)}
        onMouseOver={() => setApprovalHovered(true)}
        onMouseLeave={() => setApprovalHovered(false)}
        onClick={onStatusClick}
      >
        {approvalStatus && (
          <WorkflowIcon
            status={approvalStatus}
            size={24}
            color={approvalHovered ? 'currentColor' : ''}
          />
        )}
        <div css={styles.summaryText}>{approvalDescription}</div>
        <span>
          <Icon glyph="gem-chevron-e" ariaHidden />
        </span>
      </button>
      <button
        css={styles.summaryItem}
        onFocus={() => setDeliveryHovered(true)}
        onBlur={() => setDeliveryHovered(false)}
        onMouseOver={() => setDeliveryHovered(true)}
        onMouseLeave={() => setDeliveryHovered(false)}
        onClick={onStatusClick}
      >
        {deliveryStatus && (
          <WorkflowIcon
            status={deliveryStatus}
            size={24}
            color={deliveryHovered ? 'currentColor' : ''}
          />
        )}
        <div css={styles.summaryText}>{deliveryDescription}</div>
        <span>
          <Icon glyph="gem-chevron-e" ariaHidden />
        </span>
      </button>
    </div>
  )
}

export const WorkflowStatusSummary = (props: WorkflowStatusSummaryProps) => {
  return (
    <DefaultQueryClientProvider>
      <ThemeProvider theme="dark">
        <WorkflowStatusSummaryComponent {...props} />
      </ThemeProvider>
    </DefaultQueryClientProvider>
  )
}
